/**
 * Timezone Layer component for displaying timezone boundaries on the map
 */

import React, { useEffect, useRef, useState } from 'react';

/**
 * TimezoneLayer component - Manages timezone boundary visualization
 * @param {Object} props - Component props
 * @param {Object} props.map - Leaflet map instance
 * @param {Object} props.L - Leaflet library reference
 * @param {boolean} props.visible - Whether timezone layer should be visible
 * @param {Function} props.onLoadStateChange - Callback for loading state changes
 * @returns {null} This component doesn't render DOM elements directly
 */
const TimezoneLayer = ({ map, L, visible, onLoadStateChange }) => {
  const timezoneLayerRef = useRef(null);
  const [timezoneData, setTimezoneData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load timezone data on component mount
  useEffect(() => {
    if (timezoneData || loading) return;

    const loadTimezoneData = async () => {
      setLoading(true);
      onLoadStateChange?.(true);
      
      try {
        console.log('Loading timezone boundary data...');
        const response = await fetch('/data/combined-now.json');
        
        if (!response.ok) {
          throw new Error(`Failed to load timezone data: ${response.status}`);
        }
        
        const data = await response.json();
        setTimezoneData(data);
        console.log('Timezone data loaded successfully');
      } catch (err) {
        console.warn('Failed to load timezone data:', err.message);
        setError(err.message);
      } finally {
        setLoading(false);
        onLoadStateChange?.(false);
      }
    };

    loadTimezoneData();
  }, [timezoneData, loading, onLoadStateChange]);

  // Create and manage timezone layer
  useEffect(() => {
    if (!map || !L || !timezoneData || loading) return;

    // Remove existing layer if it exists
    if (timezoneLayerRef.current) {
      map.removeLayer(timezoneLayerRef.current);
      timezoneLayerRef.current = null;
    }

    // Create timezone layer with color-coded styling
    const timezoneLayer = L.geoJSON(timezoneData, {
      style: (feature) => {
        const tzid = feature.properties?.tzid || '';
        const offset = getTimezoneOffsetHours(tzid);
        const colors = getTimezoneColors(offset);
        
        // Debug logging for UTC+00:00 investigation
        if (Math.abs(offset) < 0.1) { // Check for values very close to 0
          console.log(`UTC+00:00 zone detected: ${tzid}, calculated offset: ${offset}`);
        }
        
        // Special background color for UTC+00:00 zones and UK/Ireland (light orange)
        // Include UK/Ireland regardless of current DST offset
        if (Math.abs(offset) < 0.1 || tzid.includes('Europe/London') || tzid.includes('Europe/Dublin') || tzid.includes('Atlantic/Reykjavik')) {
          return {
            color: '#CD853F', // Darker hue of the light orange background (Peru color)
            weight: 1,
            opacity: 0.2,
            fillColor: '#FFE4B5', // Light orange for UTC+00:00 zones, UK/Ireland, and Iceland
            fillOpacity: 0.15,
            interactive: false
          };
        }
        
        return {
          color: colors.border,
          weight: 1,
          opacity: 0.2,
          fillColor: colors.fill,
          fillOpacity: 0.08,
          interactive: false
        };
      },
      onEachFeature: (feature, layer) => {
        // No interactive elements - just display the timezone boundaries
      }
    });

    // Set z-index to ensure timezone layer appears below markers
    if (timezoneLayer.setZIndex) {
      timezoneLayer.setZIndex(1);
    }

    timezoneLayerRef.current = timezoneLayer;

    // Add to map if visible
    if (visible) {
      timezoneLayer.addTo(map);
    }

    // Cleanup function
    return () => {
      if (timezoneLayerRef.current) {
        map.removeLayer(timezoneLayerRef.current);
        timezoneLayerRef.current = null;
      }
    };
  }, [map, L, timezoneData, loading]);

  // Handle visibility changes
  useEffect(() => {
    if (!map || !timezoneLayerRef.current) return;

    if (visible) {
      if (!map.hasLayer(timezoneLayerRef.current)) {
        timezoneLayerRef.current.addTo(map);
      }
    } else {
      if (map.hasLayer(timezoneLayerRef.current)) {
        map.removeLayer(timezoneLayerRef.current);
      }
    }
  }, [map, visible]);

  // This component doesn't render anything directly
  return null;
};

/**
 * Helper function to get timezone offset string
 * @param {string} tzid - Timezone identifier (e.g., 'America/New_York')
 * @returns {string} Formatted offset string (e.g., '-05:00')
 */
const getTimezoneOffset = (tzid) => {
  try {
    // Use Intl.DateTimeFormat for accurate timezone offset calculation
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en', {
      timeZone: tzid,
      timeZoneName: 'longOffset'
    });
    
    const parts = formatter.formatToParts(now);
    const offsetPart = parts.find(part => part.type === 'timeZoneName');
    
    if (offsetPart && offsetPart.value.match(/GMT[+-]\d{1,2}:\d{2}/)) {
      return offsetPart.value.replace('GMT', '');
    }
    
    // Fallback method using getTimezoneOffset
    const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(now.toLocaleString('en-US', { timeZone: tzid }));
    const diffMinutes = (tzDate.getTime() - utcDate.getTime()) / (1000 * 60);
    
    const hours = Math.floor(Math.abs(diffMinutes) / 60);
    const minutes = Math.abs(diffMinutes) % 60;
    const sign = diffMinutes >= 0 ? '+' : '-';
    
    return `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  } catch (error) {
    console.warn('Error calculating timezone offset for', tzid, error);
    return '+00:00';
  }
};

/**
 * Helper function to get timezone offset in hours (numeric)
 * @param {string} tzid - Timezone identifier
 * @returns {number} Offset in hours
 */
const getTimezoneOffsetHours = (tzid) => {
  try {
    const offsetString = getTimezoneOffset(tzid);
    const match = offsetString.match(/([+-])(\d{2}):(\d{2})/);
    
    if (match) {
      const sign = match[1] === '+' ? 1 : -1;
      const hours = parseInt(match[2], 10);
      const minutes = parseInt(match[3], 10);
      return sign * (hours + minutes / 60);
    }
    
    return 0;
  } catch (error) {
    console.warn('Error calculating timezone offset hours for', tzid, error);
    return 0;
  }
};

/**
 * Helper function to convert HSL to hex color
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} Hex color string
 */
const hslToHex = (h, s, l) => {
  l /= 100;
  const a = s * Math.min(l, 1 - l) / 100;
  const f = n => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

/**
 * Helper function to get colors based on timezone offset
 * @param {number} offsetHours - UTC offset in hours
 * @returns {Object} Color scheme for the timezone
 */
const getTimezoneColors = (offsetHours) => {
  // Convert offset to array index (offset range: -12 to +14, total 27 possible values)
  const offsetIndex = offsetHours + 12; // Convert to 0-24 range
  
  // Distribute hues evenly across the color spectrum (360 degrees)
  // Special focus on American continent timezones for better contrast
  const hueDistribution = [
    0,    // Red (-12)
    25,   // Red-Orange (-11)
    45,   // Orange (-10)
    65,   // Yellow-Orange (-9)
    30,   // Orange-Red (-8) - Pacific Time - Changed for better contrast
    120,  // Green (-7) - Mountain Time - Changed for better contrast  
    200,  // Light Blue (-6) - Central Time - Changed for better contrast
    280,  // Purple (-5) - Eastern Time - Changed for better contrast
    180,  // Cyan (-4) - Atlantic Time
    230,  // Blue (-3) - South America
    255,  // Blue-Purple (-2)
    300,  // Purple-Pink (-1)
    350,  // Pink (0 - UTC) - Changed from 280 to avoid conflict
    15,   // Red-Pink (+1)
    85,   // Yellow (+2) - Moved from other position
    105,  // Yellow-Green (+3)
    130,  // Green (+4)
    155,  // Blue-Green (+5)
    40,   // Orange (+6) - Moved from other position
    70,   // Yellow (+7) - Moved from other position
    145,  // Teal (+8)
    170,  // Cyan-Green (+9)
    195,  // Sky Blue (+10)
    220,  // Blue (+11)
    245,  // Blue-Violet (+12)
    270,  // Violet (+13)
    330   // Magenta (+14) - Changed from 295
  ];
  
  // Special handling for UTC (offset 0) - use neutral gray
  if (Math.abs(offsetHours) < 0.1) {
    return {
      fill: '#708090',    // Slate Gray
      border: '#2F4F4F'   // Dark Slate Gray (darker hue)
    };
  }
  
  // Get hue for this timezone, ensuring we don't go out of bounds
  const hueIndex = Math.min(Math.max(offsetIndex, 0), hueDistribution.length - 1);
  const hue = hueDistribution[hueIndex];
  
  // Use consistent saturation and lightness for fill colors
  const fillSaturation = 65;  // Medium saturation for good visibility
  const fillLightness = 75;   // Light enough to see markers on top
  
  // Border colors use same hue but much darker (lower lightness)
  const borderSaturation = 80; // Slightly higher saturation for borders
  const borderLightness = 45;  // Much darker for clear border definition
  
  const fillColor = hslToHex(hue, fillSaturation, fillLightness);
  const borderColor = hslToHex(hue, borderSaturation, borderLightness);
  
  return {
    fill: fillColor,
    border: borderColor
  };
};

/**
 * Helper function to get current time in specified timezone
 * @param {string} tzid - Timezone identifier
 * @returns {string} Formatted local time
 */
const getCurrentTimeInTimezone = (tzid) => {
  try {
    const now = new Date();
    return now.toLocaleString('en-US', {
      timeZone: tzid,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      weekday: 'short'
    });
  } catch (error) {
    return 'Time unavailable';
  }
};

export default TimezoneLayer;
